// Admin Products Section - Product purchase records dashboard
// Sub-admin page for viewing product purchase data with line chart and summary stats
// Matches Stripe admin layout: header, chart controls, chart, summary, table

import React, { useState, useCallback } from 'react';
import { MainLayout } from '../../../../components/Layout';
import { LoadingPanel, RevoltTable, RevoltTableCell, DropdownSelect } from '../../../../components';
import { AdminPageHeader } from '../../components';
import { AdminProductsManager } from './managers/admin-products-manager';
import { useManager } from '../../../../core';
import { ProductTransactionChart, ProductSummaryStats } from './components';
import './AdminProductsSection.css';

const PAGE_SIZE_OPTIONS = [5, 25, 100];

export function AdminProductsSection() {
  const { manager, isInitialized } = useManager(AdminProductsManager);
  const [chartView, setChartView] = useState('all');
  const [amountMode, setAmountMode] = useState('actual');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [, setTableVersion] = useState(0);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await manager.refreshPurchases();
    } finally {
      setIsRefreshing(false);
    }
  }, [manager]);

  const handlePageChange = useCallback((delta) => {
    const page = manager.getPage();
    const limit = manager.getLimit();
    const total = manager.getTotal();
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const nextPage = Math.max(1, Math.min(totalPages, page + delta));
    if (nextPage !== page) {
      manager.setPage(nextPage);
      setTableVersion(v => v + 1);
    }
  }, [manager]);

  const handlePageSizeChange = useCallback((val) => {
    const newLimit = Number(val);
    if (PAGE_SIZE_OPTIONS.includes(newLimit)) {
      manager.setLimit(newLimit);
      setTableVersion(v => v + 1);
    }
  }, [manager]);

  const opts = { chartView, amountMode };

  if (!isInitialized) {
    return (
      <MainLayout contentClassName="main-content--admin">
        <div className="loading-container">
          <LoadingPanel
            title="Loading Product Purchases"
            subtitle="Setting up purchase dashboard..."
            showAppName={false}
          />
        </div>
      </MainLayout>
    );
  }

  const chartData = manager.getChartData(opts);
  const summaryStats = manager.getSummaryStats(opts);
  const purchases = manager.getPurchases();
  const page = manager.getPage();
  const limit = manager.getLimit();
  const total = manager.getTotal();
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const tableRows = purchases.map((p) => {
    const type = manager.getPurchaseType(p);
    const typeLabel = type === 'paid' ? 'Paid' : type === 'promo-100' ? '100% Promo' : 'Free';
    const productName = p.metadata?.productName || '—';
    const promoCode = p.metadata?.promotionCode?.code || '—';
    const purchasedAt = p.purchasedAt
      ? new Date(p.purchasedAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
      : '—';

    return [
      <RevoltTableCell key="date">{purchasedAt}</RevoltTableCell>,
      <RevoltTableCell key="product">{productName}</RevoltTableCell>,
      <RevoltTableCell key="user">{p.userEmail || p.userId || '—'}</RevoltTableCell>,
      <RevoltTableCell key="amount">{manager.formatAmount(p.amount, p.currency)}</RevoltTableCell>,
      <RevoltTableCell key="discount">{manager.formatAmount(p.discountAmount, p.currency)}</RevoltTableCell>,
      <RevoltTableCell key="promo">{promoCode}</RevoltTableCell>,
      <RevoltTableCell key="type">
        <span className={`admin-products-type-badge admin-products-type-${type}`}>{typeLabel}</span>
      </RevoltTableCell>
    ];
  });

  return (
    <MainLayout contentClassName="main-content--admin">
      <AdminPageHeader />

      <div className="admin-products-container">
        <div className="admin-section-content">
          <div className="admin-section-header">
            <h2>Product Purchases</h2>
            <p>Product purchase revenue over time and summary statistics.</p>
          </div>

          {/* Chart controls - matches Stripe layout */}
          <div className="stripe-chart-controls">
            <div className="stripe-chart-toggle-group">
              <span className="stripe-chart-toggle-label">Group by:</span>
              <div className="stripe-chart-toggle">
                <button
                  type="button"
                  className={`stripe-toggle-btn ${chartView === 'all' ? 'active' : ''}`}
                  onClick={() => setChartView('all')}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`stripe-toggle-btn ${chartView === 'byProduct' ? 'active' : ''}`}
                  onClick={() => setChartView('byProduct')}
                >
                  By Product
                </button>
              </div>
            </div>
            <div className="stripe-chart-toggle-group">
              <span className="stripe-chart-toggle-label">Amount:</span>
              <div className="stripe-chart-toggle">
                <button
                  type="button"
                  className={`stripe-toggle-btn ${amountMode === 'actual' ? 'active' : ''}`}
                  onClick={() => setAmountMode('actual')}
                >
                  Actual Paid
                </button>
                <button
                  type="button"
                  className={`stripe-toggle-btn ${amountMode === 'full' ? 'active' : ''}`}
                  onClick={() => setAmountMode('full')}
                >
                  Full (Pre-Discount)
                </button>
              </div>
            </div>
            <button
              type="button"
              className="stripe-refresh-btn"
              onClick={handleRefresh}
              disabled={isRefreshing || manager.isLoading}
            >
              {isRefreshing || manager.isLoading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          {/* Line chart */}
          <div className="stripe-chart-section">
            <ProductTransactionChart
              chartData={chartData}
              chartView={chartView}
              getProductColor={(id) => manager.getProductColor(id)}
            />
          </div>

          {/* Summary stats below */}
          <div className="stripe-summary-section">
            <ProductSummaryStats
              stats={summaryStats}
              chartView={chartView}
              getProductColor={(id) => manager.getProductColor(id)}
            />
          </div>

          {/* Purchase records table */}
          <div className="admin-products-table-section">
            <h4 className="admin-products-table-title">Purchase Records</h4>
            <RevoltTable
              headers={['Date', 'Product', 'User', 'Amount', 'Discount', 'Promo Code', 'Type']}
              gridColumns={[1.2, 1.5, 1.2, 0.8, 0.8, 0.8, 0.9]}
              rows={tableRows}
              emptyMessage="No product purchases yet."
            />
            {total > 0 && (
              <div className="admin-products-pagination">
                <div className="admin-products-page-size">
                  <label htmlFor="admin-products-per-page">Per page:</label>
                  <DropdownSelect
                    id="admin-products-per-page"
                    value={limit}
                    options={PAGE_SIZE_OPTIONS}
                    onValueChange={handlePageSizeChange}
                  />
                </div>
                <span className="admin-products-page-range">
                  Showing {(page - 1) * limit + 1}-{Math.min(page * limit, total)} of {total}
                </span>
                <button
                  type="button"
                  className="admin-products-page-btn"
                  onClick={() => handlePageChange(-1)}
                  disabled={page <= 1}
                >
                  ← Prev
                </button>
                <span className="admin-products-page-label">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  className="admin-products-page-btn"
                  onClick={() => handlePageChange(1)}
                  disabled={page >= totalPages}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
